import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { Button } from "@/shared/components/shadcn/Button";

interface DefaultBusinessGuardProps {
  children: React.ReactNode;
}

export function DefaultBusinessGuard({ children }: DefaultBusinessGuardProps) {
  const navigate = useNavigate();

  const { businessId, businesses, locationId, locations, loading } =
    useCurrentBusiness();

  const navigateToBusinessId = React.useMemo(() => {
    if (!businessId && businesses.length > 0) {
      return businesses[0].id; // TODO: Change to default when default flag is added
    }
    return undefined;
  }, [businessId, businesses]);

  const navigateToLocationId = React.useMemo(() => {
    if (businessId && !locationId && locations.length > 0) {
      return locations[0].id; // TODO: Change to default when default flag is added
    }
    return undefined;
  }, [businessId, locationId, locations]);

  useEffect(() => {
    if (navigateToBusinessId && !businessId) {
      navigate(`/b/${navigateToBusinessId}`, { replace: true });
    }
  }, [navigateToBusinessId, businessId, navigate]);

  useEffect(() => {
    if (navigateToLocationId && businessId && !locationId) {
      navigate(`/b/${businessId}/l/${navigateToLocationId}`, {
        replace: true,
      });
    }
  }, [navigateToLocationId, businessId, locationId, navigate]);

  if (loading) {
    return <Page><PageHeader title="Workspace" /><PageContent><LoadingSpinner message="Loading workspace..." /></PageContent></Page>;
  }

  if (businessId && !businesses.some((business) => business.id === businessId)) {
    return (
      <div className="h-dvh">
        <Page>
          <PageHeader title="Business not found" />
          <PageContent>
            <EmptyState message="This business is not available in your account." />
            <Button className="mt-4" onClick={() => navigate("/", { replace: true })}>Open your workspace</Button>
          </PageContent>
        </Page>
      </div>
    );
  }

  if (locationId && !locations.some((location) => location.id === locationId)) {
    return (
      <div className="h-dvh">
        <Page>
          <PageHeader title="Location not found" />
          <PageContent>
            <EmptyState message="This location is not available in the selected business." />
            <Button className="mt-4" onClick={() => navigate(`/b/${businessId}`, { replace: true })}>Open your business</Button>
          </PageContent>
        </Page>
      </div>
    );
  }

  // All checks passed, render app normally
  return <>{children}</>;
}
